import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import toast from "react-hot-toast";
import { branchService } from "../services/branchService";
import { baselineService } from "../services/baselineService";
import { getStoredClientId } from "../utils/tokenUtils";
import { Skeleton } from "./common/Skeleton";

const BranchFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  projectId,
  clientId,
  baselineId = null,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    baseline_id: baselineId || "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [baselines, setBaselines] = useState([]);
  const [loadingBaselines, setLoadingBaselines] = useState(false);

  useEffect(() => {
    if (isOpen && projectId && clientId) {
      loadBaselines();
      if (initialData) {
        setFormData({
          name: initialData.name || "",
          description: initialData.description || "",
          baseline_id: initialData.baseline_id || baselineId || "",
        });
      } else {
        setFormData({
          name: "",
          description: "",
          baseline_id: baselineId || "",
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData, projectId, clientId, baselineId]);

  const loadBaselines = async () => {
    try {
      setLoadingBaselines(true);
      const effectiveClientId = clientId || getStoredClientId();
      const response = await baselineService.getBaselines(
        projectId,
        effectiveClientId,
        { status: "published" },
      );

      // Handle different response structures
      let baselinesList = [];

      // Check for structure: { success: true, message: { message: "...", data: [...] }, data: null }
      if (response?.message?.data && Array.isArray(response.message.data)) {
        baselinesList = response.message.data;
      }
      // Check for structure: { data: [...] }
      else if (response?.data) {
        if (Array.isArray(response.data)) {
          baselinesList = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          baselinesList = response.data.data;
        } else if (
          response.data?.baselines &&
          Array.isArray(response.data.baselines)
        ) {
          baselinesList = response.data.baselines;
        }
      }
      // Check if response is directly an array
      else if (Array.isArray(response)) {
        baselinesList = response;
      }

      console.log("BranchFormModal - Loaded baselines:", baselinesList); // Debug log
      console.log("BranchFormModal - Response:", response); // Debug log
      setBaselines(baselinesList);
    } catch (error) {
      console.error("Failed to load baselines:", error);
      setBaselines([]);
    } finally {
      setLoadingBaselines(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate
      const newErrors = {};
      if (!formData.name.trim()) {
        newErrors.name = "Branch name is required";
      }
      if (!initialData && !formData.baseline_id) {
        newErrors.baseline_id = "Baseline is required";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      const effectiveClientId = clientId || getStoredClientId();

      if (initialData) {
        // Update existing branch (only if active)
        if (initialData.status !== "active") {
          toast.error(
            `Cannot update ${initialData.status} branch. Only active branches can be updated.`,
          );
          setLoading(false);
          return;
        }

        await branchService.updateBranch(
          initialData.branch_id,
          projectId,
          effectiveClientId,
          {
            name: formData.name.trim(),
            description: formData.description.trim() || null,
          },
        );
        toast.success("Branch updated successfully!");
      } else {
        // Create new branch
        await branchService.createBranch({
          baseline_id: formData.baseline_id,
          project_id: projectId,
          client_id: effectiveClientId,
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        });
        toast.success("Branch created successfully!");
      }

      if (onSubmit) {
        await onSubmit();
      }
      onClose();
    } catch (error) {
      console.error("Error saving branch:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save branch";
      toast.error(errorMessage);
      if (error.response?.data?.code === "DUPLICATE_BRANCH_NAME") {
        setErrors({
          name: "A branch with this name already exists for this baseline",
        });
      }
      if (error.response?.data?.code === "BASELINE_NOT_PUBLISHED") {
        setErrors({
          baseline_id: "Branches can only be created from published baselines",
        });
      }
      if (error.response?.data?.code === "BRANCH_NOT_ACTIVE") {
        toast.error("Only active branches can be updated.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isActive = initialData?.status === "active";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {initialData ? "Edit Branch" : "Create New Branch"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {initialData && !isActive && (
          <div className="p-4 bg-yellow-50 border-b border-yellow-200">
            <p className="text-sm text-yellow-800">
              This branch is {initialData.status} and cannot be edited. Only
              active branches can be updated.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!initialData && (
            <div>
              <Label htmlFor="baseline_id" className="text-gray-700">
                Baseline <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.baseline_id}
                onValueChange={(value) =>
                  handleSelectChange("baseline_id", value)
                }
                disabled={loading || loadingBaselines}
              >
                <SelectTrigger
                  className={`mt-1 ${errors.baseline_id ? "border-red-300" : ""}`}
                >
                  <SelectValue placeholder="Select a published baseline" />
                </SelectTrigger>
                <SelectContent>
                  {loadingBaselines ? (
                    <div className="px-2 py-2 space-y-2">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-[90%]" />
                    </div>
                  ) : baselines.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-gray-500">
                      No published baselines available
                    </div>
                  ) : (
                    baselines.map((baseline) => (
                      <SelectItem
                        key={baseline.baseline_id}
                        value={baseline.baseline_id}
                      >
                        {baseline.name} (v{baseline.version})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.baseline_id && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.baseline_id}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Branches can only be created from published baselines
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="name" className="text-gray-700">
              Branch Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading || (initialData && !isActive)}
              className={`mt-1 ${errors.name ? "border-red-300" : ""}`}
              placeholder="Enter branch name"
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-700">
              Description
            </Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading || (initialData && !isActive)}
              rows={3}
              className="mt-1"
              placeholder="Enter branch description (optional)"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              onClick={onClose}
              disabled={loading}
              variant="outline"
            >
              Cancel
            </Button>
            {(isActive || !initialData) && (
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Saving..."
                  : initialData
                    ? "Update Branch"
                    : "Create Branch"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BranchFormModal;
